// ===========================================
// HOOKS: Conceptos por responsable — ORR-230 fase 2
// ===========================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { conceptosService, type AsignarConceptoInput, type TipoConcepto } from '../services/conceptosService'
import { notificacionesService } from '../services/notificacionesService'
import { PLANTILLAS_NOTIFICACION } from '../types/notificaciones'
import type { NotificacionOrigen } from '../types/notificaciones'
import { destinatarioDeNotificacion } from '@/components/clientes/reparto/conceptosDelCliente'
import { useSupabase } from '@/lib/supabase-context'
import { rutaFichaCliente } from '@/lib/ruta-cliente'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso, handleMutationError } from '@/lib/permisos-guard'
import { useAuditor } from '@/hooks/useAuditor'
import { calendarioKeys } from './useCalendario'
import { clientesKeys } from '@/hooks/useClientes'

export const conceptosKeys = {
  all: ['conceptos-responsable'] as const,
  porCliente: (clienteId: string) => [...conceptosKeys.all, 'cliente', clienteId] as const,
  porOrganizacion: (orgId: string | null) => [...conceptosKeys.all, 'organizacion', orgId] as const,
}

/**
 * Conceptos repartidos de un cliente.
 *
 * Una lista vacía es el estado normal, no un error: mientras nadie reparta nada,
 * todos los responsables del cliente ven todos sus conceptos.
 */
export function useConceptosCliente(clienteId?: string) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: conceptosKeys.porCliente(clienteId ?? ''),
    queryFn: () => conceptosService.listarPorCliente(supabase!, organizacionId!, clienteId!),
    enabled: Boolean(supabase && organizacionId && clienteId),
  })
}

/**
 * El reparto completo de la organización, para scopear la planilla del calendario.
 *
 * Vacío es el estado normal: mientras nadie reparta nada, cada responsable ve todos los
 * conceptos de sus clientes.
 */
export function useConceptosOrganizacion() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: conceptosKeys.porOrganizacion(organizacionId),
    queryFn: () => conceptosService.listarPorOrganizacion(supabase!, organizacionId!),
    enabled: Boolean(supabase && organizacionId),
  })
}

/**
 * Invalida lo que el reparto cambia de lugar: la lista propia, el detalle del
 * cliente y la planilla del calendario, que es donde se ve quién tiene qué.
 */
const useInvalidarReparto = () => {
  const queryClient = useQueryClient()

  return (clienteId: string) => {
    // `all` es prefijo de `porCliente` y de `porOrganizacion`: alcanza la lista del cliente
    // que se está editando y el reparto que scopea la planilla.
    queryClient.invalidateQueries({ queryKey: conceptosKeys.all })
    queryClient.invalidateQueries({ queryKey: clientesKeys.detail(clienteId) })
    queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
  }
}

/**
 * Datos que solo alimentan la notificación al asignado (ORR-303). No viajan a la base:
 * `mutationFn` los descarta antes de llamar al service.
 */
interface DatosNotificacionConcepto {
  usuarioEmail?: string
  conceptoNombre?: string
  clienteNombre?: string
}

export function useAsignarConcepto() {
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const { crear } = useAuditor()
  const invalidar = useInvalidarReparto()

  return useMutation({
    mutationFn: ({
      usuarioEmail: _usuarioEmail,
      conceptoNombre: _conceptoNombre,
      clienteNombre: _clienteNombre,
      ...input
    }: Omit<AsignarConceptoInput, 'organizacionId'> & DatosNotificacionConcepto) => {
      assertPermiso(puede, 'asignarConceptos')
      return conceptosService.asignar(supabase!, { ...input, organizacionId: organizacionId! })
    },
    onSuccess: async (data, variables) => {
      invalidar(variables.clienteId)
      crear('concepto_responsable', data.id, {
        cliente_id: variables.clienteId,
        usuario_id: variables.usuarioId,
      })

      // Delegarle un concepto es darle trabajo de un cliente que puede no ser suyo: se entera.
      // Best-effort — un fallo acá NO puede tumbar una asignación que ya quedó guardada.
      const actorEmail = user?.email
      const destinatario = destinatarioDeNotificacion(variables.usuarioEmail, actorEmail)
      if (!destinatario || !actorEmail) return

      try {
        const plantilla = PLANTILLAS_NOTIFICACION.conceptoAsignado(
          variables.conceptoNombre ?? 'Un concepto',
          variables.clienteNombre ?? 'un cliente',
        )
        const origen: NotificacionOrigen = {
          canal: 'web',
          actor_email: actorEmail,
          tabla_fuente: 'cliente_responsable_conceptos',
          registro_id: data.id,
        }
        await notificacionesService.notificar(supabase!, {
          destinatarios: destinatario,
          titulo: plantilla.titulo,
          mensaje: plantilla.mensaje,
          tipo: plantilla.tipo,
          enlace: rutaFichaCliente(variables.clienteId),
          origen,
        })
      } catch (err) {
        console.error('Error notificando la asignación de concepto:', err)
      }
    },
    onError: (error) => handleMutationError(error, 'Error al asignar el concepto'),
  })
}

export function useDesasignarConcepto() {
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const { eliminar } = useAuditor()
  const invalidar = useInvalidarReparto()

  return useMutation({
    mutationFn: ({ id }: { id: string; clienteId: string }) => {
      assertPermiso(puede, 'asignarConceptos')
      return conceptosService.desasignar(supabase!, id)
    },
    onSuccess: (_, variables) => {
      invalidar(variables.clienteId)
      eliminar('concepto_responsable', variables.id, {})
    },
    onError: (error) => handleMutationError(error, 'Error al quitar la asignación'),
  })
}

/**
 * ORR-317 — Cuántos clientes tiene hoy un concepto, para el preview antes de confirmar el
 * reparto en masa. Solo se activa con un concepto elegido: sin eso no hay nada que contar.
 */
export function useClientesConConcepto(tipo: TipoConcepto | null, conceptoId: string | null) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: [...conceptosKeys.all, 'clientes-del-concepto', tipo, conceptoId],
    queryFn: () => conceptosService.clientesConConcepto(supabase!, organizacionId!, tipo!, conceptoId!),
    enabled: Boolean(supabase && organizacionId && tipo && conceptoId),
  })
}

/**
 * ORR-317 — Reparto en masa: asigna un impuesto o una obligación recurrente a un responsable
 * en todos los clientes que lo tienen hoy (snapshot, opción A de D-15). No cuelga del detalle
 * de un cliente porque no es la acción de uno: por eso invalida el reparto completo de la
 * organización en vez de un cliente puntual.
 */
export function useAsignarConceptoEnMasa() {
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      usuarioEmail: _usuarioEmail,
      conceptoNombre: _conceptoNombre,
      ...input
    }: {
      usuarioId: string
      tipo: TipoConcepto
      conceptoId: string
      // Solo para la notificación, igual que en useAsignarConcepto: no viajan a la base.
      usuarioEmail?: string
      conceptoNombre?: string
    }) => {
      assertPermiso(puede, 'asignarConceptos')
      return conceptosService.asignarEnMasa(supabase!, { ...input, organizacionId: organizacionId! })
    },
    onSuccess: async ({ clientes }, variables) => {
      queryClient.invalidateQueries({ queryKey: conceptosKeys.all })
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })

      if (clientes === 0) {
        toast.success('Ningún cliente activo tiene este concepto todavía')
        return
      }
      toast.success(`Asignado en ${clientes} cliente${clientes === 1 ? '' : 's'}`)

      // Mismo criterio que useAsignarConcepto: se entera de que tiene trabajo nuevo. Una
      // sola notificación con la cantidad, no una por cliente (ver conceptoAsignadoEnMasa).
      // Best-effort — un fallo acá no puede tumbar un reparto que ya quedó guardado.
      const actorEmail = user?.email
      const destinatario = destinatarioDeNotificacion(variables.usuarioEmail, actorEmail)
      if (!destinatario || !actorEmail) return

      try {
        const plantilla = PLANTILLAS_NOTIFICACION.conceptoAsignadoEnMasa(
          variables.conceptoNombre ?? 'Un concepto',
          clientes,
        )
        const origen: NotificacionOrigen = {
          canal: 'web',
          actor_email: actorEmail,
          tabla_fuente: 'cliente_responsable_conceptos',
          registro_id: variables.conceptoId,
        }
        await notificacionesService.notificar(supabase!, {
          destinatarios: destinatario,
          titulo: plantilla.titulo,
          mensaje: plantilla.mensaje,
          tipo: plantilla.tipo,
          enlace: '/clientes',
          origen,
        })
      } catch (err) {
        console.error('Error notificando el reparto en masa:', err)
      }
    },
    onError: (error) => handleMutationError(error, 'Error al asignar en masa'),
  })
}
